import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Expense } from '../data/expenses';
import {
  getActiveSubscription,
  isPushSupported,
  sendTestPush,
  subscribeToPush,
  syncBills,
  unsubscribeFromPush,
} from '../data/push';
import type { PushBill } from '../data/push';

interface PushRemindersProps {
  /** Bills of the currently viewed month — synced to the worker on change. */
  bills: Expense[];
}

type BellState =
  | 'checking' // still reading the existing subscription
  | 'unsupported' // no Push API here (non-PWA Safari / dev / older iOS)
  | 'off' // supported, permission not granted yet
  | 'on' // subscribed — reminders active
  | 'denied'; // permission blocked

const PushReminders: React.FC<PushRemindersProps> = ({ bills }) => {
  const [state, setState] = useState<BellState>('checking');
  const [hint, setHint] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const subRef = useRef<PushSubscription | null>(null);
  const hintTimer = useRef<number | undefined>(undefined);

  const showHint = useCallback((text: string) => {
    setHint(text);
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setHint(null), 5000);
  }, []);

  // On mount: figure out the current subscription state.
  useEffect(() => {
    let cancelled = false;
    if (!isPushSupported()) {
      setState('unsupported');
      return;
    }
    getActiveSubscription().then((sub) => {
      if (cancelled) return;
      subRef.current = sub;
      setState(sub ? 'on' : Notification.permission === 'denied' ? 'denied' : 'off');
    });
    return () => {
      cancelled = true;
      window.clearTimeout(hintTimer.current);
    };
  }, []);

  // Keep the worker's bill list in sync (debounced) while subscribed.
  useEffect(() => {
    const sub = subRef.current;
    if (!sub) return;
    const t = window.setTimeout(() => {
      const pushBills: PushBill[] = bills.map((b) => ({
        id: b.id,
        title: b.title,
        date: b.date,
        completed: b.completed,
        remindAt: b.remindAt,
        dueSoonDays: b.dueSoonDays,
      }));
      void syncBills(sub, pushBills);
    }, 800);
    return () => window.clearTimeout(t);
  }, [bills, state]);

  const handleToggle = async () => {
    const current = subRef.current;

    if (state === 'on' && current) {
      // Bell on → tap turns reminders off.
      subRef.current = null;
      setState('off');
      void unsubscribeFromPush(current);
      showHint('Reminders off — bills will no longer be pushed.');
      return;
    }

    if (state === 'checking') return; // subscription state still loading — ignore taps

    if (state === 'unsupported') {
      showHint(
        'Push reminders need iOS 16.4+ with the app added to your Home Screen.'
      );
      return;
    }
    if (state === 'denied') {
      showHint('Notifications are blocked. Allow them in iOS Settings → Bills → Notifications.');
      return;
    }

    // Ask for permission (must be in this user gesture), then subscribe.
    let permission: NotificationPermission;
    try {
      permission = await Notification.requestPermission();
    } catch {
      showHint('This browser cannot show notifications for this app.');
      return;
    }
    if (permission !== 'granted') {
      setState('denied');
      showHint('Notifications were not enabled.');
      return;
    }

    const sub = await subscribeToPush();
    if (!sub) {
      showHint('Reminders are not ready yet — is the reminder server deployed?');
      return;
    }
    subRef.current = sub;
    setState('on');
    const pushBills: PushBill[] = bills.map((b) => ({
      id: b.id,
      title: b.title,
      date: b.date,
      completed: b.completed,
      remindAt: b.remindAt,
      dueSoonDays: b.dueSoonDays,
    }));
    void syncBills(sub, pushBills);
    showHint(
      'Reminders on — due-today and heads-up pushes at each bill\u2019s reminder time, plus a 9pm nightly summary.'
    );
  };

  /** Ask the worker to push a test notification right now (no waiting for 8am). */
  const handleTestPush = async () => {
    const sub = subRef.current;
    if (!sub || sending) return;
    setSending(true);
    const result = await sendTestPush(sub);
    setSending(false);
    if (result === 'sent') {
      showHint('Test notification sent — check your notification centre.');
    } else if (result === 'resubscribe') {
      showHint(
        'Your notification link is out of date — tap the bell to turn reminders off and on again.'
      );
    } else {
      showHint('Could not reach the reminder server — check your connection and try again.');
    }
  };

  return (
    <>
      <button
        type="button"
        className={`bell ${state === 'on' ? 'bell--on' : ''} ${state === 'denied' ? 'bell--denied' : ''}`}
        onClick={handleToggle}
        aria-label={state === 'on' ? 'Turn off reminders' : 'Turn on reminders'}
        title={
          state === 'on'
            ? 'Reminders on — tap to turn off'
            : state === 'unsupported'
              ? 'Push reminders unavailable here'
              : 'Reminders off — tap to turn on'
        }
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
          <path
            d="M12 3a6 6 0 0 0-6 6c0 4-1.5 5.5-2.5 6.5h17C19.5 14.5 18 13 18 9a6 6 0 0 0-6-6z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M10 19.5a2.2 2.2 0 0 0 4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          {state === 'denied' && (
            <path d="M5 5l14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          )}
        </svg>
      </button>

      {state === 'on' && (
        <button
          type="button"
          className="btn-icon bell-test"
          onClick={handleTestPush}
          disabled={sending}
          aria-label="Send test push"
          title="Send a test notification now"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path
              d="M13 2 4.5 12.5H11L9.5 22 19 10.5h-6.5L13 2z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {hint && (
        <div className="push-hint" role="status" onClick={() => setHint(null)}>
          {hint}
        </div>
      )}
    </>
  );
};

export default PushReminders;
